import { FC, useState } from 'react';
import { useGetAllComments, useCreateReply } from '../../functions/commentHook';
import { toast } from 'react-hot-toast';

interface Reply {
  content: string;
}

interface Blog {
  title: string;
  _id: string;
}

interface Comment {
  _id: string;
  content: string;
  createdAt: string;
  blogId: Blog;
  replies: Reply[];
}

const CommentsList: FC<{ blogId: string }> = ({ blogId }) => {
  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [visibleReplies, setVisibleReplies] = useState<Set<string>>(new Set());
  const limit: number = 5;

  const { comments = [], isLoading, isError, refetch } = useGetAllComments(blogId, currentPage, limit);
  const { mutate: createReply, isLoading: isReplying } = useCreateReply();

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        {[...Array(5)].map((_, index) => (
          <div key={index} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div className="h-4 bg-gradient-to-r from-gray-100 to-gray-200 w-1/3 rounded-full"></div>
              <div className="h-3 bg-gray-100 w-1/4 rounded-full"></div>
            </div>
            <div className="space-y-2">
              <div className="h-3 bg-gray-100 rounded-full w-full"></div>
              <div className="h-3 bg-gray-100 rounded-full w-4/5"></div>
              <div className="h-3 bg-gray-100 rounded-full w-3/4"></div>
            </div>
            <div className="mt-4 h-3 bg-gray-100 w-1/6 rounded-full"></div>
          </div>
        ))}
      </div>
    );
  }

  if (isError && comments.length === 0) {
    return (
      <div className="text-center py-8 px-6 bg-blue-50 rounded-2xl shadow-inner border border-blue-100">
        <div className="mb-4 inline-block bg-blue-100 p-4 rounded-full">
          <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-gray-800 mb-2">No comments yet</h3>
        <p className="text-gray-600 max-w-md mx-auto">
          Start the conversation! Be the first to share your thoughts about this post.
        </p>
      </div>
    );
  }

  const handleReplyClick = (commentId: string) => {
    setSelectedCommentId(commentId);
  };

  const handleReplySubmit = (commentId: string) => {
    if (!replyContent.trim()) {
      toast.error('Please write a reply before submitting.');
      return;
    }

    createReply(
      { commentId, replyData: { content: replyContent } },
      {
        onSuccess: () => {
          toast.success('Reply submitted successfully!');
          setReplyContent('');
          setSelectedCommentId(null);
          refetch();
        },
        onError: (err) => {
          toast.error('Failed to submit reply: ' + err.message);
        }
      }
    );
  };

  const toggleRepliesVisibility = (commentId: string) => {
    setVisibleReplies((prevState) => {
      const newState = new Set(prevState);
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      newState.has(commentId) ? newState.delete(commentId) : newState.add(commentId);
      return newState;
    });
  };

  return (
    <div className="space-y-8">
      {comments.map((comment: Comment) => (
        <div key={comment._id} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:border-blue-100 transition-all">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
                <span className="text-blue-600 font-medium text-sm">
                  {comment.blogId.title[0].toUpperCase()}
                </span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">{decodeURIComponent(comment.blogId.title)}</h4>
                <p className="text-sm text-gray-500">
                  {new Date(comment.createdAt).toLocaleDateString('en-US', {
                    dateStyle: 'medium'
                  })}
                </p>
              </div>
            </div>
          </div>

          <p className="text-gray-700 leading-relaxed mb-4 pl-2 border-l-2 border-blue-200">
            {decodeURIComponent(comment.content)}
          </p>

          <div className="flex items-center gap-4">
            <button type='button'
              onClick={() => handleReplyClick(comment._id)}
              className="flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
              </svg>
              Reply
            </button>

            {comment.replies.length > 0 && (
              <button type='button'
                onClick={() => toggleRepliesVisibility(comment._id)}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                <svg className={`w-4 h-4 transition-transform ${visibleReplies.has(comment._id) ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                {comment.replies.length} {comment.replies.length === 1 ? 'reply' : 'replies'}
              </button>
            )}
          </div>

          {selectedCommentId === comment._id && (
            <div className="mt-4 space-y-3 pl-8 border-l-2 border-blue-100">
              <textarea
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                className="w-full p-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                placeholder="Write your thoughtful reply..."
                rows={3}
              />
              <div className="flex items-center gap-3">
                <button type='button'
                  onClick={() => handleReplySubmit(comment._id)}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                  disabled={isReplying}
                >
                  {isReplying ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      Post Reply
                    </>
                  )}
                </button>
                <button type='button'
                  onClick={() => setSelectedCommentId(null)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {visibleReplies.has(comment._id) && comment.replies.length > 0 && (
            <div className="mt-4 space-y-4 pl-8 border-l-2 border-blue-100">
              {comment.replies.map((reply, index) => (
                <div key={index} className="group relative pt-4">
                  <div className="absolute left-0 top-6 w-4 h-px bg-gray-200"></div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center mt-1">
                      <svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-gray-700 leading-relaxed bg-gray-50 p-3 rounded-lg">
                        {decodeURIComponent(reply.content)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      <div className="flex justify-between items-center mt-8">
        <button type='button'
          onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          disabled={currentPage <= 1}
          className="px-5 py-2.5 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Previous
        </button>
        <span className="text-sm text-gray-500">Page {currentPage}</span>
        <button type='button'
          onClick={() => setCurrentPage((prev) => prev + 1)}
          disabled={comments.length < limit}
          className="px-5 py-2.5 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex items-center gap-2"
        >
          Next
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default CommentsList;